import { ApiService } from '../api/api.service';
import { AppRoute } from '../../enums';
import { BiometricAuthService } from '../../services';
import { Capacitor } from '@capacitor/core';
import { CredentialsService } from '../credentials/credentials.service';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { ResetPassword, ServerResponse } from '../../interfaces';
import { Router } from '@angular/router';
import { UserService } from '../../authentication';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly apiService = inject(ApiService);
  private readonly biometricAuthService = inject(BiometricAuthService);
  private readonly credentialsService = inject(CredentialsService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly userUrl = 'users/';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  private get api(): {
    get: <T = any>(url: string) => Observable<T>;
    post: <T = any>(url: string, body?: any, reqOpts?: any) => Observable<T>;
    put: <T = any>(url: string, body?: any, reqOpts?: any) => Observable<T | unknown>;
  } {
    return {
      get: this.apiService.restGet.bind(this.apiService),
      post: this.apiService.post.bind(this.apiService),
      put: this.apiService.put.bind(this.apiService),
    };
  }

  /* eslint-enable @typescript-eslint/no-explicit-any */

  public autoChangeEmail(code: string): Observable<ServerResponse | unknown> {
    return this.api.put(`${this.userUrl}EmailAutoChange/${code}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public changeEmailForUser(data: { newEmail: string; emailRepeated: string; password: string }): Observable<any> {
    return this.apiService.post(`${this.userUrl}changeemail`, {
      email: data.newEmail,
      emailRepeated: data.emailRepeated,
      password: data.password,
    });
  }

  public async changePassword(data: { oldPassword: string; password: string; passwordMatch: string }): Promise<void> {
    await firstValueFrom(this.apiService.post(`${this.userUrl}changepassword`, data));
    await this.credentialsService.resetPin();
    if (Capacitor.isNativePlatform() && this.biometricAuthService.isBiometricLoginActivated()) {
      await this.biometricAuthService.deactivateBiometricLogin();
    }
    await this.router.navigate([AppRoute.Logout]);
  }

  public createUser(accountInfo: {
    email: string;
    personalNumber: string;
  }): Observable<{ success: boolean; data: number }> {
    return this.api
      .post(`${this.userUrl}`, {
        eMail: accountInfo.email,
        personalNumber: accountInfo.personalNumber.trim(),
      })
      .pipe(
        map((value) => {
          if (value.success === false) {
            throw value;
          }
          return value;
        }),
      );
  }

  public deleteUser(password: string): Observable<ServerResponse> {
    return this.apiService.deleteUser(password) as Observable<ServerResponse>;
  }

  public getNewPassword(personalNumber: string): Observable<{ success: boolean; data: number; message: string }> {
    return this.api.post(`${this.userUrl}${personalNumber}/reset-password`);
  }

  public requestNewValidationLink(pn: string): Observable<string | unknown> {
    return this.api.put(`${this.userUrl}${pn}/reset-activation-code`);
  }

  public sendNewPinForUser(password: string): Observable<boolean> {
    return this.apiService
      .post<boolean>(`${this.userUrl}newpin`, { password })
      .pipe(map((response) => typeof response === 'boolean' && response));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public setNewPassword(data: ResetPassword): Observable<any> {
    const { code, password, passwordRepeated, personalNumber } = data;
    return this.api.put(`${this.userUrl}${personalNumber}/password`, {
      code,
      password,
      passwordRepeated,
    });
  }

  public validateEmailAndSetPassword(validationData: {
    personalNumber: string;
    password: string;
    code: string;
    passwordRepeated: string; // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Observable<any> {
    const path = `${this.userUrl}validate`;
    const payload = {
      personalNumber: validationData.personalNumber.trim(),
      pin: validationData.code.trim(),
      password: validationData.password,
      passwordRepeated: validationData.passwordRepeated,
    };

    return this.api.post(path, payload);
  }
}
